/**
 * KanbanCardCrmSection - Seção de dados CRM no card do Kanban de Conversas
 * 
 * Exibe informações do último negócio (deal) e contrato quando o módulo CRM
 * está habilitado e o toggle CRM está ativo.
 * 
 * Versão: 1.0.0 - 2026-03-07
 */

import { Briefcase, FileSignature } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { CONTRACT_STATUS_LABELS, type ContractStatus } from '@/types/contracts';
import type { ContactCrmData } from '@/hooks/useKanbanConversationCrmData';

/**
 * Formata valor monetário de forma compacta
 * Ex: R$ 1.500,00 → R$ 1,5K | R$ 150,00 → R$ 150
 */
const formatCompactCurrency = (value: number): string => {
  if (value >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toFixed(1).replace('.0', '')}M`;
  }
  if (value >= 1_000) {
    return `R$ ${(value / 1_000).toFixed(1).replace('.0', '')}K`;
  }
  return `R$ ${value.toFixed(0)}`;
};

/**
 * Labels e cores para status do deal
 */
const DEAL_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  open: { label: 'Aberto', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  won: { label: 'Ganho', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  lost: { label: 'Perdido', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
};

interface KanbanCardCrmSectionProps {
  crmData: ContactCrmData;
  /** Callback ao clicar na seção CRM - abre modal de deal */
  onCrmClick?: (dealId: string) => void;
}

export function KanbanCardCrmSection({ crmData, onCrmClick }: KanbanCardCrmSectionProps) {
  const statusConfig = DEAL_STATUS_CONFIG[crmData.deal_status] || DEAL_STATUS_CONFIG.open;
  const contractStatusInfo = crmData.last_contract_status 
    ? CONTRACT_STATUS_LABELS[crmData.last_contract_status as ContractStatus] 
    : null;

  // Trunca título do deal
  const dealTitle = crmData.deal_title?.length > 18 
    ? crmData.deal_title.slice(0, 18) + '...' 
    : crmData.deal_title;

  /**
   * Handler de clique na seção CRM
   * Usa stopPropagation para não disparar o onClick do card (conversa)
   */
  const handleCrmClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCrmClick?.(crmData.deal_id);
  };

  return (
    <div 
      className="mt-1.5 pt-1.5 border-t border-border/30 space-y-1 cursor-pointer hover:bg-muted/50 rounded-sm transition-colors -mx-1 px-1"
      onClick={handleCrmClick}
      title="Clique para abrir detalhes do negócio"
    >
      {/* Linha 1: Ícone + Título do deal + Status badge + Contador extra */}
      <div className="flex items-center gap-1 min-w-0">
        <Briefcase className="h-3 w-3 text-muted-foreground shrink-0" />
        <span 
          className="text-[10px] font-medium text-foreground truncate flex-1"
          title={crmData.deal_title}
        >
          {dealTitle}
        </span>
        <span className={cn(
          'text-[9px] font-medium px-1 py-0.5 rounded-full shrink-0',
          statusConfig.className
        )}>
          {statusConfig.label}
        </span>
        {/* Contador de deals open extras */}
        {crmData.extra_open_deals_count > 0 && (
          <Badge 
            variant="secondary" 
            className="text-[9px] px-1 py-0 h-3.5 shrink-0"
            title={`+${crmData.extra_open_deals_count} negócio(s) em aberto`}
          >
            +{crmData.extra_open_deals_count}
          </Badge>
        )}
      </div>

      {/* Linha 2: Pipeline > Fase (com cor) + Valor */}
      <div className="flex items-center justify-between gap-1 min-w-0">
        <div className="flex items-center gap-1 min-w-0 overflow-hidden">
          {/* Indicador de cor da fase */}
          {crmData.deal_phase_color && (
            <span 
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: crmData.deal_phase_color }}
            />
          )}
          <span className="text-[10px] text-muted-foreground truncate max-w-[120px]">
            {crmData.deal_pipeline_name && crmData.deal_phase_name
              ? `${crmData.deal_pipeline_name} › ${crmData.deal_phase_name}`
              : crmData.deal_phase_name || crmData.deal_pipeline_name || ''}
          </span>
        </div>
        {/* Valor do deal */}
        {crmData.deal_value > 0 && (
          <span className="text-[10px] font-semibold text-foreground shrink-0">
            {formatCompactCurrency(crmData.deal_value)}
          </span>
        )}
      </div>

      {/* Linha 3: Badge do último contrato (se houver) */}
      {contractStatusInfo && (
        <div className="flex items-center gap-1 min-w-0 overflow-hidden">
          <FileSignature className="h-2.5 w-2.5 text-muted-foreground shrink-0" />
          <span className={cn(
            'text-[10px] font-medium px-1.5 py-0.5 rounded-full truncate max-w-[100px]',
            contractStatusInfo.color
          )}>
            {contractStatusInfo.label}
          </span>
          {crmData.last_contract_title && (
            <span className="text-[10px] text-muted-foreground truncate">
              {crmData.last_contract_title.length > 15 
                ? crmData.last_contract_title.slice(0, 15) + '...' 
                : crmData.last_contract_title}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
